import React, { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import Clipboard from '@react-native-community/clipboard';
import client from '../../../../graphql/client';
import { useAuthState } from '../../../auth/authContext';
import LinearGradientButton from '../LinearGradientButton';
import TextInput from '../TextInput';
import View from '../View';
import ThumbsButton from '../ThumbsButton';
import { UriInfo } from '../../../types/types';
import styles from './styles';
import { feedbackValidationSchema } from './validationSchema';
import {
  FEEDBACK_INPUT_PLACEHOLDER,
  SUBMIT_TEXT,
  THANK_YOU,
} from './constants';
import GalanoGrotesqueText from '../GalanoGrotesqueText';

export type FeedbackBoxProps = {
  currentSessionId: string;
  fanId: string;
  isReturningFan: boolean;
  feedbackComplete: boolean;
  handleFeedbackComplete?: (e: boolean) => void;
  uriInfo: UriInfo;
};

export const FeedbackBox: FC<FeedbackBoxProps> = (props) => {
  const { cognitoId } = useAuthState();
  const [feedback, setFeedback] = useState<string>('');
  const [likeId, setLikeId] = useState('');
  const [isLiked, setLike] = useState(false);
  const [isDisliked, setDislike] = useState(false);
  const {
    currentSessionId,
    fanId,
    isReturningFan,
    feedbackComplete,
    handleFeedbackComplete,
    uriInfo,
  } = props;

  const { id: currentSpotifyId } = uriInfo || {};

  // TODO: break out to Like container
  const checkAndSetLikes = (fanData) => {
    const currentLike = fanData.allLikes.items.find(
      (item) => item?.sessionId == currentSessionId,
    );
    if (currentLike?.likeValue === 1) {
      setLikeId(currentLike.id);
      setLike(true);
    } else if (currentLike?.likeValue === -1) {
      setLikeId(currentLike.id);
      setDislike(true);
    } else if (!currentLike) {
      setDislike(false);
      setLike(false);
    }
  };

  const updateFan = async () => {
    await client.updateFanData({ cognitoId: fanId, isReturningFan: true });
  };

  // TODO: break out into feedbackbox container
  const giveFeedback = async (values) => {
    try {
      const feedbackDetails = {
        comment: values.feedback,
        fanId,
        sessionId: currentSessionId,
      };

      await client.leaveFeedback(feedbackDetails);
      handleFeedbackComplete && handleFeedbackComplete(true);
      if (!isReturningFan) {
        await updateFan();
      }
    } catch (e) {
      // TODO: Add Monitoring here
      console.error(e);
    }
  };

  // TODO: break out to Like container
  const handleLikeButtonChange = async (likeType: string) => {
    let likeValue = 0;
    if (likeType === 'up') {
      if (!isLiked) {
        likeValue = 1;
        setLike(true);
      } else {
        likeValue = 0;
        setLike(false);
      }
      setDislike(false);
    } else if (likeType === 'down') {
      if (!isDisliked) {
        likeValue = -1;
        setDislike(true);
      } else {
        likeValue = 0;
        setDislike(false);
      }
      setLike(false);
    }
    await setLikeToDB(likeValue);
  };

  // TODO: break out to Like container
  const setLikeToDB = async (likeValue: number) => {
    let likeData;
    if (likeId) {
      const updateLikeDetails = {
        likeId,
        likeValue,
      };
      likeData = await client.updateALike(updateLikeDetails);
      return likeData;
    } else {
      // call the create
      const likeDetails = {
        fanId: cognitoId,
        sessionId: currentSessionId,
        likeValue,
      };
      likeData = await client.likeASong(likeDetails);
      setLikeId(likeData.id);
      return likeData;
    }
  };

  useEffect(() => {
    async function checkFeedback() {
      const existingFeedback = await client.getFeedbackByFanAndSession({
        sessionId: currentSessionId,
        fanId,
      });
      if (existingFeedback) {
        const comment = existingFeedback.comment;
        setFeedback(comment);
        handleFeedbackComplete && handleFeedbackComplete(true);
      } else {
        setFeedback('');
        handleFeedbackComplete && handleFeedbackComplete(false);
      }
    }
    checkFeedback();
  }, [fanId, currentSpotifyId, feedbackComplete]);

  useEffect(() => {
    async function getFanData() {
      const retrievedFanData = await client.getFanData(cognitoId);
      checkAndSetLikes(retrievedFanData);
    }

    getFanData();
  }, [fanId, currentSpotifyId]);

  return (
    <View>
      <Formik
        initialValues={{
          feedback,
        }}
        onSubmit={(values) => giveFeedback(values)}
        enableReinitialize={true}
        validationSchema={feedbackValidationSchema}
      >
        {({
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isValid,
          touched,
          values,
        }) => (
          <View>
            <View style={styles.feedbackContainer}>
              <View style={styles.thumbsContainer}>
                <ThumbsButton
                  isActive={isLiked}
                  likeType="up"
                  onPress={handleLikeButtonChange}
                />
                <ThumbsButton
                  isActive={isDisliked}
                  likeType="down"
                  onPress={handleLikeButtonChange}
                />
              </View>
              <TextInput
                autoCapitalize="sentences"
                autoCompleteType="off"
                autoCorrect
                keyboardType="default"
                label="Feedback"
                labelHidden
                multiline
                numberOfLines={4}
                onBlur={handleBlur('feedback')}
                onChangeText={handleChange('feedback')}
                onSelectionChange={() => Clipboard.setString('')}
                placeholder={FEEDBACK_INPUT_PLACEHOLDER}
                value={values.feedback}
                editable={!feedbackComplete}
                errorMessage={touched.feedback && errors.feedback}
              />
            </View>
            {!feedbackComplete && (
              <LinearGradientButton
                disabled={!isValid}
                title={SUBMIT_TEXT}
                onPress={handleSubmit}
              />
            )}
          </View>
        )}
      </Formik>
      {feedbackComplete && (
        <View style={styles.thankYouContainer}>
          <GalanoGrotesqueText style={styles.thankYouText}>
            {THANK_YOU}
          </GalanoGrotesqueText>
        </View>
      )}
    </View>
  );
};

export default FeedbackBox;
